import { useKoreanUnitSeparators } from '@flight-workspace/common-client/src/util/number';
import { days } from 'common/utils';
import { IPassengers } from 'contexts/searchParams';

export enum Unit {
  PERCENT = 'percent',
  KRW = 'krw',
}

export enum CardDiscountRange {
  ONLY_BASIC_FARE = 'F',
  EXCLUDE_PUBLISH_FEE = 'T',
  TOTAL_FARE = 'S',
}

export enum PaymentMethod {
  NAVER_PAY = 'B74',
}

export enum PromotionType {
  NAVER_PAY = 'npay',
  CASHBACK = 'C',
  DISCOUNT = 'I',
  DISCOUNT_CASHBACK = 'IC',
  DISCOUNT_STATEMENT_CREDIT = 'IS',
  DISCOUNT_STATEMENT_CREDIT_CASHBACK = 'ISC',
  STATEMENT_CREDIT = 'S',
  STATEMENT_CREDIT_CASHBACK = 'SC',
  DEFAULT = 'default',
}

export enum Route {
  DOMESTIC = 'DOMESTIC',
  INTERNATIONAL = 'INTERNATIONAL',
}

export interface ICodeName {
  code: string;
  name?: string;
}

export interface IBenefit {
  unit: Unit;
  value: number;
  limit?: number;
}

export interface IBanner {
  active: boolean;
  position: string;
  imageUrl: string;
  bgColor?: string;
  linkedUrl: string;
  logCode?: String;
}

export interface IPromotion {
  departureStartDate?: Date;
  departureEndDate?: Date;
  fareTypes?: ICodeName[];
  paymentMethods?: ICodeName[];
  otas?: string[];
  airlines?: string[];
  originAirports?: string[];
  destinationAirports?: string[];
  nonstopOnly?: boolean;
  tripTypes?: { OW: boolean; RT: boolean };
  passengers: { adult: boolean; child: boolean; infant: boolean };
  cabinClasses?: { Y: boolean; P: boolean; C: boolean; F: boolean };
  benefit: IBenefit;
  description?: string;
  banners?: IBanner[];
}

export interface IDiscountInfo {
  cardType: ICodeName;
  discountType: PromotionType;
  discountRange: CardDiscountRange;
  amount: IBenefit;
}

export interface IDiscountFare {
  adultDiscountFare: number;
  childDiscountFare: number;
  discountInfo: IDiscountInfo;
}

export interface IDiscountsByType {
  [Unit.PERCENT]: number;
  [Unit.KRW]: number;
  totalDiscount: number;
}

interface ISummaryByType {
  type: string;
  summary: string;
}

export interface IGroupedPromotion {
  otaId: string;
  paymentMethod?: string;
  summaryByType: ISummaryByType[];
  discountedPrice: number;
}

export type PromotionByPaymentMethod = Map<
  string | undefined,
  [string | undefined, Map<PromotionType, IDiscountsByType>]
>;

export interface IMergedPromotions {
  descriptions: { description: string; totalDiscount: number }[];
  promotionMap: PromotionByPaymentMethod;
}

export const defaultNaverPayPromotion: IPromotion = {
  benefit: { unit: Unit.PERCENT, value: 1, limit: 100000 },
  description:
    '네이버페이 결제 시 네이버페이 포인트 1% 적립 대상 항공편입니다.',
  passengers: { adult: true, child: true, infant: true },
  paymentMethods: [{ code: PaymentMethod.NAVER_PAY, name: '네이버페이' }],
};

export const suffixMap = new Map([
  [PromotionType.NAVER_PAY, '적립'],
  [PromotionType.CASHBACK, '캐시백'],
  [PromotionType.DISCOUNT, '즉시할인'],
  [PromotionType.DISCOUNT_CASHBACK, '할인혜택(즉시할인 & 캐시백)'],
  [PromotionType.DISCOUNT_STATEMENT_CREDIT, '할인혜택(즉시할인 & 청구할인)'],
  [
    PromotionType.DISCOUNT_STATEMENT_CREDIT_CASHBACK,
    '할인혜택(즉시할인 & 청구할인 & 캐시백)',
  ],
  [PromotionType.STATEMENT_CREDIT, '청구할인'],
  [PromotionType.STATEMENT_CREDIT_CASHBACK, '할인혜택(청구할인 & 캐시백)'],
  [PromotionType.DEFAULT, '할인'],
]);

export const isCombinedBenefit = (benefit: IDiscountsByType) =>
  !(benefit[Unit.PERCENT] || benefit[Unit.KRW]);

export function getDiscountStr([type, discountInfo]: [
  PromotionType,
  IDiscountsByType,
]) {
  const strs = [];
  if (discountInfo[Unit.PERCENT]) strs.push(`${discountInfo[Unit.PERCENT]}%`);
  if (discountInfo[Unit.KRW]) {
    strs.push(`${useKoreanUnitSeparators(discountInfo[Unit.KRW])}원`);
  }
  const str = strs.join('+');
  return `${str ? `${str} ` : ''}${suffixMap.get(type)}${
    isCombinedBenefit(discountInfo) ? '혜택' : ''
  }`;
}

const roundDown = (number: number, unit: number = 1) =>
  Math.floor(number / unit) * unit;

export function getNPayBenefits(
  { unit, value, limit }: IBenefit,
  originalPrice: number,
  { adult, child, infant }: IPassengers = { adult: 1, child: 0, infant: 0 },
) {
  switch (unit) {
    case Unit.PERCENT: {
      const discount = roundDown(originalPrice * (value / 100));
      return limit && discount > limit ? limit : discount;
    }
    case Unit.KRW: {
      const discount = value * (adult + child + infant);
      return limit && discount > limit ? limit : discount;
    }
    default: {
      return 0;
    }
  }
}

export function getNewDiscountInfo(
  discount: IBenefit,
  partial: boolean,
  totalDiscount: number,
): IDiscountsByType {
  const discountInfo = { [Unit.PERCENT]: 0, [Unit.KRW]: 0, totalDiscount };
  if (discount.unit === Unit.KRW) {
    discountInfo[Unit.KRW] = totalDiscount;
  } else if (discount.unit === Unit.PERCENT && !partial) {
    discountInfo[Unit.PERCENT] = discount.value;
  }
  return discountInfo;
}

export function groupByPaymentMethod(
  promotionMap: PromotionByPaymentMethod,
  paymentMethod: ICodeName | undefined,
  type: PromotionType,
  benefit: IBenefit,
  totalDiscount: number,
  reset: boolean = false,
) {
  const found = promotionMap.get(paymentMethod?.code);
  const newDiscountInfo = getNewDiscountInfo(benefit, reset, totalDiscount);
  if (!found) {
    promotionMap.set(paymentMethod?.code, [
      paymentMethod?.name,
      new Map([[type, newDiscountInfo]]),
    ]);
    return promotionMap;
  }
  const [, promotions] = found;
  const discountInfo = promotions.get(type);
  if (!discountInfo) {
    promotions.set(type, newDiscountInfo);
    return promotionMap;
  }
  discountInfo.totalDiscount += totalDiscount;
  if (isCombinedBenefit(discountInfo)) return promotionMap;
  if (benefit.unit === Unit.KRW) discountInfo[Unit.KRW] += totalDiscount;
  if (benefit.unit === Unit.PERCENT) {
    if (reset) {
      discountInfo[Unit.PERCENT] = 0;
      discountInfo[Unit.KRW] = 0;
    } else discountInfo[Unit.PERCENT] += benefit.value;
  }
  return promotionMap;
}

export const isPromotionTarget = (
  promotion: IPromotion,
  {
    airlineCode,
    departureDate,
    otaId,
    supportNPay,
    departureAirport,
    arrivalAirport,
  }: {
    airlineCode: string;
    departureDate: string;
    otaId: string;
    supportNPay: boolean;
    departureAirport: string;
    arrivalAirport: string;
  },
) =>
  (!promotion.departureStartDate ||
    days(departureDate, 'YYYYMMDDHHmm').isSameOrAfter(
      promotion.departureStartDate,
    )) &&
  (!promotion.departureEndDate ||
    days(departureDate, 'YYYYMMDDHHmm').isSameOrBefore(
      promotion.departureEndDate,
    )) &&
  (!promotion.otas || promotion.otas.includes(otaId)) &&
  (!promotion.airlines || promotion.airlines.includes(airlineCode)) &&
  (!promotion.paymentMethods ||
    supportNPay ||
    promotion.paymentMethods.some(
      paymentMethod => paymentMethod.code !== PaymentMethod.NAVER_PAY,
    )) &&
  (!promotion.originAirports ||
    promotion.originAirports.includes(departureAirport)) &&
  (!promotion.destinationAirports ||
    promotion.destinationAirports.includes(arrivalAirport));

export const convertPromotionMapToArray = (
  promotionMap: PromotionByPaymentMethod,
  originalPrice: number,
  otaId: string,
) =>
  Array.from(promotionMap).map(([, [paymentMethod, promotions]]) =>
    Array.from(promotions).reduce<IGroupedPromotion>(
      (acc, discountInfo) => {
        const [type, promotion] = discountInfo;
        const summary = getDiscountStr(discountInfo);
        acc.summaryByType.push({ type, summary });
        acc.discountedPrice -= promotion.totalDiscount;
        return acc;
      },
      {
        otaId,
        paymentMethod,
        summaryByType: [],
        discountedPrice: originalPrice,
      },
    ),
  );
